import { ImageName } from "@mcleod/images";
import { Button, Checkbox, ClickEvent, Image, Label, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutYSplitStopsTable } from "./autogen/AutogenLayoutYSplitStopsTable";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { PlanningUtil } from "./planning/PlanningUtil";
import { AppointmentUtil } from "./AppointmentUtil";
import { OrderTabView } from "./OrderTabView";

const STOP_TYPE_PICKUP = "PU";
const STOP_TYPE_SPLIT_PICKUP = "SP";
const STOP_TYPE_DELIVERY = "SO";
const STOP_TYPE_SPLIT_DROP = "SD";
const PICKUP_STOP_TYPES: string[] = [STOP_TYPE_PICKUP, STOP_TYPE_SPLIT_PICKUP];
const DELIVERY_STOP_TYPES: string[] = [STOP_TYPE_DELIVERY, STOP_TYPE_SPLIT_DROP];

export class YSplitStopsTable extends AutogenLayoutYSplitStopsTable {
    public selectedStop: ModelRow = null;
    onStopSelected: (modelRow: ModelRow) => void;
    isManifest: boolean = false;
    leaveFreight: boolean = false;
    crossDock: boolean = false;
    orderId: string = null;

    tableOrderStopsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;

        this.setupStopType(tableRow, modelRow)
        AppointmentUtil.setAppointmentLabel(tableRow, "labelApptRequired", "appt_required", "confirmed");
        this.setupBadgeActual(tableRow, modelRow);
        this.setupRowTopper(tableRow, modelRow);
        this.setupStopSequence(tableRow, modelRow);
        this.setupManifestOrderLink(tableRow, modelRow);
        this.leaveFreight === true && this.setupLeaveFreightCheckbox(tableRow, modelRow);
    }

    setupManifestOrderLink(tableRow: TableRow, modelRow: ModelRow<any>) {
        if (this.isManifest && modelRow.get("order_id") != null) {
            const txtOrderId = tableRow.findComponentById("labelOrderId");
            txtOrderId.visible = true;
            txtOrderId.addUnderlineWhenMouseOver();
            txtOrderId.addClickListener(() => {
                OrderTabView.navigateTo({ key: modelRow.get("order_id"), newTab: true });
            });
        }
    }

    setupStopSequence(tableRow: TableRow, modelRow: ModelRow<any>) {
        const labelStopNumber = tableRow.findComponentById("labelStopNumber") as Label ?? null;
        if (this.isManifest) {
            labelStopNumber.text = modelRow.get("movement_sequence");
        }
    }

    setupLeaveFreightCheckbox(tableRow: TableRow, modelRow: ModelRow<any>) {
        const checkboxLeaveFreight = tableRow.findComponentById("checkboxLeaveFreight") as Checkbox ?? null;
        if (checkboxLeaveFreight) {
            const stopType = modelRow?.get("stop_type");
            const orderId = modelRow?.get("order_id");
            const status = modelRow?.get("status");
            const isDelivery = DELIVERY_STOP_TYPES.includes(stopType);
            const clearedPickups = this.sourceStop.data.filter((stop: ModelRow) => PICKUP_STOP_TYPES.includes(stop.get("stop_type")) && stop.get("status") == "D" );
            const isDeliveredWithClearPickup = clearedPickups.some((stop : ModelRow) => stop.get("order_id") === orderId);
            const isDepartedWithSplitPickupDrop = this.hasSplitPickupOrDrop(clearedPickups);
            const isCheckboxVisible = isDelivery && (isDeliveredWithClearPickup || isDepartedWithSplitPickupDrop) && status != "D";
            const enableCheckbox = StringUtil.equalsIgnoreCase(this.orderId, orderId) && isDelivery;
            checkboxLeaveFreight.visible = isCheckboxVisible;
            checkboxLeaveFreight.checked = enableCheckbox;
        }
    }

    hasSplitPickupOrDrop(stops: ModelRow[]): boolean {
        return stops.some((stop: ModelRow) => {
            const stopType = stop.get("stop_type");
            const isSplitStop = stopType === "SP" || stopType === "SD";
            return isSplitStop;
        });
    }

    setupStopType(tableRow: TableRow, modelRow: ModelRow<any>) {
        const stopType: string = modelRow.get("stop_type");
        const isPickupType = PICKUP_STOP_TYPES.includes(stopType);
        const imageStopType = tableRow.findComponentById("imageStopType") as Image ?? null;
        if (imageStopType) {
            imageStopType.name = (isPickupType)
                ? ImageName.loadPickup
                : ImageName.loadDelivery;
        }

        const textboxSchedArriveEarly = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox ?? null;
        if (textboxSchedArriveEarly) {
            textboxSchedArriveEarly.caption = isPickupType
                ? "Early Pickup"
                : "Early Delivery";
        }

        const textboxSchedArriveLate = tableRow.findComponentById("textboxSchedArriveLate") as Textbox ?? null;
        if (textboxSchedArriveLate) {
            textboxSchedArriveLate.caption = isPickupType
                ? "Late Pickup"
                : "Late Delivery";
        }

        const labelStopType = tableRow.findComponentById("labelStopType") as Label ?? null;
        if (labelStopType) {
            if ("SP" == stopType) {
                labelStopType.text = "Split Pickup";
            } else {
                // Enforce "Capital Case" on inhereted "Sentence case" classic metadata
                labelStopType.text = labelStopType.text
                    .split(' ')
                    .map(s => s[0].toUpperCase() + s.substring(1))
                    .join(' ');
            }
        }
    }

    setupBadgeActual(tableRow: TableRow, modelRow: ModelRow<any>) {
        if ("A" === modelRow.get("status") && modelRow.get("actual_arrival") != null) {
            tableRow.findComponentById("labelArrivedBadge").visible = true;
        } else if ("D" === modelRow.get("status")) {
            tableRow.findComponentById("labelDepartedBadge").visible = true;
        }
    }

    async setupRowTopper(tableRow: TableRow, modelRow: ModelRow<any>) {
        const panelRowTopper = tableRow.findComponentById("panelRowTopper");
        if (panelRowTopper) {
            if (tableRow.index === 0) {
                panelRowTopper.visible = false;
            } else {
                panelRowTopper.visible = true;
                const buttonStopSelected = tableRow.findComponentById("buttonStopSelected") as Button;
                if(buttonStopSelected != null && this.sourceStop.data.length == 2 && modelRow.get("movement_sequence") == "2") {
                    this.selectStop(tableRow);
                }
                const priorRow = tableRow.table.getRowByIndex(tableRow.index - 1);
                const priorStop = priorRow.data as ModelRow;
                const distCalcProps = {
                    first_city: modelRow.get("city_id"),
                    first_state: modelRow.get("state"),
                    first_zip: modelRow.get("zip_code"),
                    prior_city: priorStop.get("city_id"),
                    prior_state: priorStop.get("state"),
                    prior_zip: priorStop.get("zip_code"),
                };
                const labelDistBetweenStops = tableRow.findComponentById("labelDistBetweenStops") as Label;
                labelDistBetweenStops.visible = buttonStopSelected.visible = modelRow.get("stop_type") != "SP";
                if (labelDistBetweenStops) {
                    labelDistBetweenStops.text = "0 MILES BETWEEN STOPS";
                    const res = await Api.post("lme/dispatch/distance-between-stops", distCalcProps);
                    if (res.data?.[0].calc_dist_from_prev) {
                        labelDistBetweenStops.text = `${res.data?.[0].calc_dist_from_prev} MILES BETWEEN STOPS`;
                    }
                }
            }
        }
    }

    labelApptRequiredOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Label, true);
    }

    /** This is an event handler for the onClick event of buttonStopSelected. */
    buttonStopSelectedOnClick(event: ClickEvent) {
        const buttonStopSelected = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(buttonStopSelected);
        if (tableRow.data == this.selectedStop) {
            buttonStopSelected.selected = !buttonStopSelected.selected;
            this.applyStopSelectedState(buttonStopSelected, false);
            this.selectedStop = null;
            return;
        }
        this.selectStop(tableRow);
    }

    selectStop(selectedRow: TableRow) {
        this.tableOrderStops.rows.forEach((row:TableRow) => {
            const buttonStopSelected = row.findComponentById("buttonStopSelected") as Button;
            if (buttonStopSelected) {
                buttonStopSelected.selected = row === selectedRow;
                if (buttonStopSelected.selected) {
                    this.selectedStop = row.data;
                    this.applyStopSelectedState(buttonStopSelected, true);
                    if (this.onStopSelected != null) this.onStopSelected(row.data);
                } else {
                    this.applyStopSelectedState(buttonStopSelected, false);
                }
            }
        });
    }

    applyStopSelectedState(buttonStopSelected: Button, selected: boolean) {
        buttonStopSelected.applyTempState({
            caption: selected ? "Selected" : null,
            imageName: selected ? ImageName.circlePlusFilled : ImageName.add
        });
    }
}
